/**
 * Native glue shared by dictation and voice calls: opening the app's Settings page after a permission
 * denial, app background / foreground, keeping the screen awake, notification haptics and the status bar
 * over the dark call screen. Every plugin is optional (older binaries may lack one): each helper checks
 * `Capacitor.isPluginAvailable` and quietly does nothing where it can't work.
 */
import { Capacitor } from '@capacitor/core'
import { isIosNative, isNativePlatform, isTouchPrimary } from '@/utils/platform'

/** iOS can deep-link to this app's page in Settings (microphone / speech recognition switches). */
export function canOpenAppSettings(): boolean {
    return isIosNative() && Capacitor.isPluginAvailable('AppLauncher')
}

export async function openAppSettings(): Promise<boolean> {
    if (!canOpenAppSettings()) return false
    try {
        const { AppLauncher } = await import('@capacitor/app-launcher')
        const r = await AppLauncher.openUrl({ url: 'app-settings:' })
        return Boolean(r?.completed)
    } catch {
        return false
    }
}

/**
 * The app went to the background (`pause`) or came back (`resume`). Native: the App plugin's pause /
 * resume (not "inactive": iOS resigns active for a permission prompt or Control Center). Web: only when
 * `web` is set, from page visibility on touch devices (a desktop tab keeps going in the background).
 * Returns the unsubscribe.
 */
export function onAppLifecycle(h: { pause?: () => void; resume?: () => void }, opts: { web?: boolean } = {}): () => void {
    if (isNativePlatform()) {
        if (!Capacitor.isPluginAvailable('App')) return () => {}
        const handles: { remove: () => Promise<void> }[] = []
        let off = false
        void import('@capacitor/app').then(async ({ App }) => {
            const add = [
                h.pause ? App.addListener('pause', () => h.pause!()) : null,
                h.resume ? App.addListener('resume', () => h.resume!()) : null,
            ].filter(Boolean) as Promise<{ remove: () => Promise<void> }>[]
            for (const p of add) {
                const handle = await p
                if (off) void handle.remove()
                else handles.push(handle)
            }
        }).catch(() => {})
        return () => {
            off = true
            for (const handle of handles.splice(0)) void handle.remove()
        }
    }
    if (!opts.web || typeof document === 'undefined' || !isTouchPrimary()) return () => {}
    const onVis = () => (document.visibilityState === 'hidden' ? h.pause?.() : h.resume?.())
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
}

let wakeLock: { release: () => Promise<void> } | null = null
let wantAwake = false

function onVisibleAgain() {
    if (wantAwake && document.visibilityState === 'visible' && !wakeLock) void requestWebWakeLock()
}

async function requestWebWakeLock() {
    try {
        const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void>; addEventListener?: (e: string, fn: () => void) => void }> } }).wakeLock
        if (!wl) return
        const lock = await wl.request('screen')
        wakeLock = lock
        lock.addEventListener?.('release', () => { if (wakeLock === lock) wakeLock = null })
    } catch {
        /* denied (hidden page, low battery): the call still works */
    }
}

/** Keep the screen on for the call (KeepAwake in the app, the Screen Wake Lock API on the web). */
export async function keepAwake(on: boolean): Promise<void> {
    wantAwake = on
    if (isNativePlatform()) {
        if (!Capacitor.isPluginAvailable('KeepAwake')) return
        try {
            const { KeepAwake } = await import('@capacitor-community/keep-awake')
            await (on ? KeepAwake.keepAwake() : KeepAwake.allowSleep())
        } catch {
            /* not supported on this device */
        }
        return
    }
    if (typeof document === 'undefined') return
    if (on) {
        document.addEventListener('visibilitychange', onVisibleAgain)
        if (!wakeLock) await requestWebWakeLock()
    } else {
        document.removeEventListener('visibilitychange', onVisibleAgain)
        const lock = wakeLock
        wakeLock = null
        await lock?.release().catch(() => {})
    }
}

/** Success / warning / error haptics (useHaptics covers impact and selection). */
export async function notifyHaptic(type: 'success' | 'warning' | 'error'): Promise<void> {
    if (!isNativePlatform() || !Capacitor.isPluginAvailable('Haptics')) return
    try {
        const { Haptics, NotificationType } = await import('@capacitor/haptics')
        const map = { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error }
        await Haptics.notification({ type: map[type] })
    } catch {
        /* haptics unavailable */
    }
}

/** Light status bar text over the dark call screen; back to the theme's after. */
export async function callStatusBar(on: boolean): Promise<void> {
    if (!isNativePlatform() || !Capacitor.isPluginAvailable('StatusBar')) return
    try {
        const { StatusBar, Style } = await import('@capacitor/status-bar')
        const dark = document.documentElement.classList.contains('dark')
        await StatusBar.setStyle({ style: on || dark ? Style.Dark : Style.Light })
    } catch {
        /* status bar unavailable */
    }
}
